import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

export default function Login() {
  const { user, loading, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from?.pathname || "/";

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "var(--bg-app)" }}
      >
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (user) {
    const target = user.must_change_password ? "/change-password" : from;
    return <Navigate to={target} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Benvenuto ${u.full_name}`);
      nav(u.must_change_password ? "/change-password" : from, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Credenziali non valide";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "var(--bg-app)" }}
      data-testid="login-page"
    >
      <div
        className="w-full max-w-[380px] rounded-xl p-8"
        style={{
          background: "var(--bg-surface)",
          border: "1px solid var(--border-subtle)",
          boxShadow: "var(--shadow-lg)",
        }}
      >
        <div className="flex items-center gap-2.5 mb-7">
          <Logo size={32} />
          <div>
            <div
              className="font-semibold text-[17px] tracking-tight"
              style={{ color: "var(--text-primary)" }}
            >
              TechQuote
            </div>
            <div
              className="text-[12px]"
              style={{ color: "var(--text-muted)" }}
            >
              Accesso operatori
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="login-form">
          <div>
            <label
              className="block text-[11px] font-medium mb-1 uppercase tracking-wide"
              style={{ color: "var(--text-secondary)" }}
            >
              Email
            </label>
            <input
              type="email"
              autoFocus
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-10 px-3 rounded-md text-[14px] outline-none transition-colors"
              style={{
                border: "1px solid var(--border-default)",
                background: "var(--bg-surface)",
                color: "var(--text-primary)",
              }}
              data-testid="login-email"
            />
          </div>
          <div>
            <label
              className="block text-[11px] font-medium mb-1 uppercase tracking-wide"
              style={{ color: "var(--text-secondary)" }}
            >
              Password
            </label>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-10 px-3 rounded-md text-[14px] outline-none"
              style={{
                border: "1px solid var(--border-default)",
                background: "var(--bg-surface)",
                color: "var(--text-primary)",
              }}
              data-testid="login-password"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !email || !password}
            className="w-full h-10 rounded-md text-[14px] font-medium mt-2 disabled:opacity-60"
            style={{
              background: "var(--accent-primary)",
              color: "var(--text-on-accent)",
            }}
            data-testid="login-submit"
          >
            {busy ? "Accesso…" : "Accedi"}
          </button>
        </form>

        <div
          className="text-[11px] text-center mt-6"
          style={{ color: "var(--text-muted)" }}
        >
          Problemi con l'accesso? Contatta l'amministratore.
        </div>
      </div>
    </div>
  );
}
